import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { BuildingAuthorityType } from '../models/building-authority-type';
import { GlobalAuthority } from '../models/global-authority';
import { User } from '../models/user.interface';
import { UserService } from './user/user.service';
import { ManagingCompanyService } from '@services/managing-company.service';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  constructor(
    private userService: UserService,
    private route: ActivatedRoute,
    private managingCompanyService: ManagingCompanyService
  ) {}

  public isAuthorizedForBuilding(target: BuildingAuthorityType, buildingId: number): Observable<boolean> {
    return this.userService.getCurrentUser().pipe(map((user) => this.hasBuildingAuthority(user, target, buildingId)));
  }

  public isRoleAuthorizedForBuilding(target: BuildingAuthorityType[], buildingId: number): Observable<boolean> {
    return this.userService
      .getCurrentUser()
      .pipe(map((user) => this.hasAnyBuildingAuthority(user, target, buildingId)));
  }

  public getUserManagingCompanyIds(): Observable<number[]> {
    return this.userService.getCurrentUser().pipe(map((user) => user.managingCompanyIds));
  }

  public canSeeTenantMadeTags(buildingId: number): Observable<boolean> {
    return this.userService.getCurrentUser().pipe(
      map((user) => {
        if (user.tenantsMap[buildingId].length > 0) {
          return true;
        } else {
          return this.hasBuildingAuthority(user, BuildingAuthorityType.MANAGE_TENANT, buildingId);
        }
      })
    );
  }

  private hasBuildingAuthority(user: User, target: BuildingAuthorityType, buildingId: number): boolean {
    return (
      user.buildingAuthorizations.filter(
        (authorization) =>
          authorization.buildingId == buildingId &&
          authorization.authorities.filter((authority) => authority.toString() === target.value).length > 0
      ).length > 0
    );
  }

  private hasAnyBuildingAuthority(user: User, target: BuildingAuthorityType[], buildingId: number): boolean {
    return (
      user.buildingAuthorizations.filter(
        (authorization) =>
          authorization.buildingId === buildingId &&
          authorization.authorities.filter(
            (authority) => target.filter((auth) => auth.value === authority.toString()).length > 0
          ).length > 0
      ).length > 0
    );
  }

  public hasOnlyAnalyticsViewCapability(buildingId: number): Observable<boolean> {
    return this.userService
      .getCurrentUser()
      .pipe(
        map((user) =>
          user.buildingAuthorizations.some(
            (authorization) =>
              authorization.buildingId === buildingId &&
              authorization.authorities.some(
                (authority) => authority.toString() === BuildingAuthorityType.VIEW_DASHBOARD_ANALYTICS.value
              ) &&
              !authorization.authorities.some(
                (authority) => authority.toString() === BuildingAuthorityType.VIEW_DASHBOARD_INSIGHTS.value
              )
          )
        )
      );
  }

  private hasManagingCompaniesAuthorities(
    user: User,
    userManagingCompanyAuthorities: string[],
    validMcIds: Set<number>
  ): boolean {
    return (user.managingCompanyAuthorities ?? []).some((mcAuthorities) => {
      if (!validMcIds.has(mcAuthorities.managingCompanyId)) {
        return false;
      }

      return (mcAuthorities.authorities ?? []).some((mcAuthority) =>
        userManagingCompanyAuthorities.includes(mcAuthority)
      );
    });
  }

  private hasManagingCompaniesAuthority(
    user: User,
    userManagingCompanyAuthority: string,
    validMcIds: Set<number>
  ): boolean {
    return (user.managingCompanyAuthorities ?? []).some((mcAuthorities) => {
      if (!validMcIds.has(mcAuthorities.managingCompanyId)) return false;

      return (mcAuthorities.authorities ?? []).some((mcAuthority) => mcAuthority === userManagingCompanyAuthority);
    });
  }

  /**
   * This helper method is useful to know if `admin` flag has been passed as a query param
   */
  public isAdminRequested(): Observable<boolean> {
    return this.route.queryParams.pipe(map((queryParams) => queryParams.admin != null));
  }

  public isAuthorized(requestedAuthority: GlobalAuthority): Observable<boolean> {
    if (requestedAuthority == GlobalAuthority.VIEW_HIDDEN_COMPONENTS) {
      return this.route.queryParams.pipe(map((queryParams) => queryParams.admin != null));
    }

    return this.userService.getCurrentUser().pipe(map((user) => this.hasGlobalAuthority(user, requestedAuthority)));
  }

  private hasGlobalAuthority(user: User, requestedAuthority: GlobalAuthority): boolean {
    return user.globalAuthorities.filter((authority) => authority == requestedAuthority).length > 0;
  }

  public isAuthorizedForManagingCompany(userManagingCompanyAuthority: string): Observable<boolean> {
    return this.userService.getCurrentUser().pipe(
      switchMap((user) =>
        this.managingCompanyService.getManagingCompanies().pipe(
          map((companies) => {
            const validMcIds = new Set((companies ?? []).map((mc) => mc.id));
            return this.hasManagingCompaniesAuthority(user, userManagingCompanyAuthority, validMcIds);
          })
        )
      )
    );
  }

  public isAuthorizedForAnyManagingCompany(userManagingCompanyAuthorities: string[]): Observable<boolean> {
    return this.userService.getCurrentUser().pipe(
      switchMap((user) =>
        this.managingCompanyService.getManagingCompanies().pipe(
          map((companies) => {
            const validMcIds = new Set((companies ?? []).map((mc) => mc.id));
            return this.hasManagingCompaniesAuthorities(user, userManagingCompanyAuthorities, validMcIds);
          })
        )
      )
    );
  }
}
